<template>
  <div>
    <div v-if="clearHistoryAction" class="d-flex justify-end mb-2 mr-2 mt-2">
      <g-button type="error" label="Clear History" :disabled="!rows.length" @onclick="openClearHistory()" />
    </div>
    <v-data-table
      disable-pagination
      disable-sort
      hide-default-footer
      class="database-table"
      :headers="headers"
      :items="rows"
      :loading="loading"
    >
      <template v-slot:body="{ items }">
        <tbody v-if="items.length">
          <tr v-for="item in items" :key="item.id" class="database-row">
            <td @click="openDetail(item)">{{ item.from }}</td>
            <td @click="openDetail(item)">{{ getTo(item) }}</td>
            <td @click="openDetail(item)">{{ getSubject(item) }}</td>
            <td @click="openDetail(item)"><EmailDeliveryStatusChip :value="item.status" /></td>
            <td @click="openDetail(item)">{{ formatDate(item.created_at) }}</td>
            <td v-if="resendEmailAction">
              <div class="d-flex align-center">
                <g-button
                  class="mr-2"
                  type="primary"
                  xsmall
                  icon="refresh"
                  label="Re-send"
                  @onclick="resendEmail(item)"
                  :disabled="item.status !== 'failed'"
                />
                <g-button
                  type="error"
                  xsmall
                  icon="delete"
                  label="Delete"
                  @onclick="confirmDeleteEmail(item)"
                  :disabled="item.status !== 'failed'"
                />
              </div>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td :colspan="headers.length" class="no-data">{{ placeholder }}</td>
          </tr>
        </tbody>
        <infinite-loading slot="append" spinner="waveDots" :identifier="infiniteId" @infinite="infiniteHandler">
          <div slot="no-more"></div>
          <div slot="no-results"></div>
        </infinite-loading>
        <EmailDeliveryDetailDialog v-if="selectedItem" v-model="emailDetailDialog" :item="selectedItem" />
        <EmailDeliveryClearHistoryDialog
          v-if="clearHistoryDialog"
          v-model="clearHistoryDialog"
          :onClear="clearHistory"
        />
        <EmailDeliveryDeleteDialog v-if="deleteDialog" v-model="deleteDialog" :onConfirm="executeDelete" />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import EmailDeliveryDetailDialog from "./EmailDeliveryDetailDialog";
import EmailDeliveryClearHistoryDialog from "./EmailDeliveryClearHistoryDialog";
import EmailDeliveryDeleteDialog from "./EmailDeliveryDeleteDialog";
import EmailDeliveryStatusChip from "./EmailDeliveryStatusChip";
import InfiniteLoading from "vue-infinite-loading";
import moment from "moment";

const PAGE_SIZE = 20;
const REFRESH_INTERVAL = 10000;

export default {
  components: {
    EmailDeliveryDetailDialog,
    EmailDeliveryClearHistoryDialog,
    EmailDeliveryDeleteDialog,
    EmailDeliveryStatusChip,
    InfiniteLoading,
  },
  props: {
    fetchAction: { type: String, required: true },
    clearHistoryAction: { type: String, required: false },
    resendEmailAction: { type: String, required: false },
    deleteEmailAction: { type: String, required: false },
    autoRefresh: { type: Boolean, default: false },
    placeholder: { type: String, default: "No data available" },
  },
  data() {
    return {
      baseHeaders: [
        { text: "From", value: "from", width: "20%", align: "center" },
        { text: "To", value: "to", width: "20%", align: "center" },
        { text: "Subject", value: "subject", width: "30%", align: "center" },
        { text: "Status", value: "status", width: "5%", align: "center" },
      ],
      rows: [],
      nextCursor: null,
      loading: false,
      infiniteId: new Date().getTime(),
      timer: null,
      emailDetailDialog: false,
      selectedItem: null,
      clearHistoryDialog: false,
      deleteDialog: false,
      itemToDelete: null,
    };
  },
  computed: {
    headers() {
      const headers = this.baseHeaders;
      if (this.resendEmailAction) {
        headers.push({ text: "Created At", value: "created_at", width: "20%", align: "center" });
        headers.push({ text: "", value: "", width: "5%", align: "center" });
      } else {
        headers.push({ text: "Created At", value: "created_at", width: "25%", align: "center" });
      }
      return headers;
    },
  },
  methods: {
    openDetail(item) {
      this.selectedItem = item;
      this.emailDetailDialog = true;
    },
    openClearHistory() {
      this.clearHistoryDialog = true;
    },
    getTo(item) {
      try {
        const recipients = JSON.parse(item.to);
        return Array.isArray(recipients) ? recipients.join(", ") : item.to;
      } catch (e) {
        return item.to;
      }
    },
    getSubject(item) {
      return item.subject || "No subject";
    },
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("MMM DD, YYYY HH:mm");
    },
    fetchData(limit, cursor) {
      return this.$store.dispatch(this.fetchAction, { limit, cursor });
    },
    fetchQueue() {
      if (this.loading) return;
      this.loading = true;

      const limit = Math.max(this.rows.length || 0, PAGE_SIZE);

      this.fetchData(limit, null)
        .then((res) => {
          const items = res.data || [];
          this.rows = items;
          this.nextCursor = res.next_cursor || null;
          this.infiniteId = new Date().getTime();
        })
        .catch((err) => {
          this.$root.$error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.timer = setInterval(() => this.fetchQueue(), REFRESH_INTERVAL);
    },
    stopAutoRefresh() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    },
    infiniteHandler($state) {
      if (this.loading) {
        $state.loaded();
        return;
      }

      this.loading = true;
      const limit = PAGE_SIZE;
      const cursor = this.nextCursor || null;

      this.fetchData(limit, cursor)
        .then((res) => {
          const items = res.data || [];

          if (!items.length) {
            $state.complete();
            return;
          }

          this.rows.push(...items);
          this.nextCursor = res.next_cursor || null;

          if (this.nextCursor) {
            $state.loaded();
          } else {
            $state.complete();
          }
        })
        .catch((err) => {
          this.$root.$error(err);
          $state.complete();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clearHistory() {
      this.$store.dispatch(this.clearHistoryAction).then(() => this.fetchQueue());
    },
    resendEmail(item) {
      this.$store.dispatch(this.resendEmailAction, { id: item.id }).then(() => this.fetchQueue());
    },
    confirmDeleteEmail(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    executeDelete() {
      if (!this.deleteEmailAction || !this.itemToDelete) return;
      this.$store.dispatch(this.deleteEmailAction, { id: this.itemToDelete.id }).then(() => {
        this.itemToDelete = null;
        this.fetchQueue();
      });
    },
  },
  mounted() {
    if (this.autoRefresh) {
      this.startAutoRefresh();
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
};
</script>

<style lang="css">
.no-data {
  text-align: center;
  padding: 16px 0;
}
</style>
